import {TFunction} from 'i18next';
import {useState, useCallback, useEffect} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {
  useGuestGroup,
  usePetFeeDeal,
  useMutateReservation,
  useReservationSettings,
  useTaxes,
} from 'hooks';
import {useGuestGroupMutation} from 'hooks/useGuestGroupMutation';
import {FORM_NAMES, MODAL_FORMS} from './constants';
import {GROUP_TYPES} from '@chekin/core';
import {OverlayLoader, ResponsiveSheet, cn} from '@chekinapp/ui';
import {Smile, UserRound} from 'lucide-react';
import {SettingsFormSkeleton} from './SettingsFormSkeleton';
import {getFields, getGroupType} from 'utils/reservationSettings';
import {FormTypes} from './types';
import {useKioskoStore} from 'context/kiosko';
import {useSummary} from 'context/summary';
import {useRemoteAccessEnabled} from 'hooks/remoteAccess';
import {usePendingPaymentSteps} from './usePendingPaymentSteps';
import {useIsReservationSettingsReady} from './useIsReservationSettingsReady';
import {NumberOfGuestsForm} from './forms/NumberOfGuestsForm';
import {WhosComingForm} from './forms/WhosComingForm';
import {ReservationTypeForm} from './forms/ReservationTypeForm';
import {ConfirmLeaderGuestForm} from './forms/ConfirmLeaderGuestForm';

type ReservationSettingsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  availableForms: Record<MODAL_FORMS, boolean>;
  ageLimit?: number | null;
  isAutoOpened?: boolean;
};

const COUNTER_ICONS = {
  [FORM_NAMES.numberOfGuests]: <UserRound size={18} />,
  [FORM_NAMES.middle]: <UserRound size={18} />,
  [FORM_NAMES.senior]: <UserRound size={18} />,
  [FORM_NAMES.junior]: <Smile size={18} />,
};

const WELCOME_DESCRIPTION = {
  steps: {keys: 'welcome_desc_steps_keys', plain: 'welcome_desc_steps'},
  register: {keys: 'welcome_desc_register_keys', plain: 'welcome_desc_register'},
} as const;

const getModalTitle = (t: TFunction) => ({
  [MODAL_FORMS.numberOfGuests]: t('change_guest_count'),
  [MODAL_FORMS.reservationType]: t('type_of_reservation'),
  [MODAL_FORMS.confirmLeaderGuest]: undefined,
});

const getModalDescription = (t: TFunction) => ({
  [MODAL_FORMS.numberOfGuests]: t('change_guest_count_hint'),
  [MODAL_FORMS.reservationType]: undefined,
  [MODAL_FORMS.confirmLeaderGuest]: undefined,
});

function ReservationSettingsModal({
  isOpen,
  onClose,
  ageLimit,
  availableForms,
  isAutoOpened,
}: ReservationSettingsModalProps) {
  const {t} = useTranslation();
  const isKiosko = useKioskoStore(state => state.connection.isConnected);
  const {seasonEntity} = useTaxes();
  const isReady = useIsReservationSettingsReady();
  const {checkCanEditReservationDetails} = useReservationSettings();
  const [isLoadingSaveGuest, setIsLoadingSaveGuest] = useState(false);
  const [currentModalForm, setCurrentModalForm] = useState(MODAL_FORMS.numberOfGuests);

  const {guestGroup, isFetching: isGuestGroupFetching} = useGuestGroup();
  const {
    updateGuestGroup: patchGuestGroup,
    isLoading: isGuestGroupMutation,
    error: guestGroupMutationError,
  } = useGuestGroupMutation();
  const {
    mutate,
    mutateAsync: updateReservation,
    isPending: isReservationMutation,
  } = useMutateReservation();
  const {createPetFeeDeal, hasPetFeeOffer, isCreatingPetFeeDeal} = usePetFeeDeal();
  const canEdit = checkCanEditReservationDetails();
  const title = getModalTitle(t)[currentModalForm];
  const description = getModalDescription(t)[currentModalForm];
  const {housingName} = useSummary();
  const hasKeys = useRemoteAccessEnabled();
  const hasPaymentSteps = usePendingPaymentSteps().length > 0;
  const isWelcome =
    Boolean(isAutoOpened) && currentModalForm === MODAL_FORMS.numberOfGuests;
  const submitLabel = isAutoOpened ? t('confirm') : t('save_changes');
  const welcomeDescription = t(
    WELCOME_DESCRIPTION[hasPaymentSteps ? 'steps' : 'register'][
      hasKeys ? 'keys' : 'plain'
    ],
  );

  const formMethods = useForm<FormTypes>();
  const {handleSubmit} = formMethods;

  const isInitialLoading = !isReady;
  const isSubmitting =
    isGuestGroupFetching ||
    isGuestGroupMutation ||
    isLoadingSaveGuest ||
    isCreatingPetFeeDeal ||
    isReservationMutation;

  const isLoading = isInitialLoading || isSubmitting;
  const disabled = isLoading;
  const groupType = guestGroup?.group_type ?? GROUP_TYPES.family;

  useEffect(
    function reinitializeCurrentModalSection() {
      if (!availableForms[MODAL_FORMS.numberOfGuests]) {
        availableForms[MODAL_FORMS.reservationType]
          ? setCurrentModalForm(MODAL_FORMS.reservationType)
          : setCurrentModalForm(MODAL_FORMS.confirmLeaderGuest);
      } else {
        setCurrentModalForm(MODAL_FORMS.numberOfGuests);
      }
    },
    [availableForms],
  );

  const updateGuestGroup = useCallback(
    (formData: FormTypes) => {
      const visibleFields = getFields({
        lessThanRule: seasonEntity.lessThanRule,
        betweenRule: seasonEntity.betweenRule,
        moreThanRule: seasonEntity.moreThanRule,
      });

      const senior = visibleFields[FORM_NAMES.senior] ? formData[FORM_NAMES.senior] : 0;
      const middle = visibleFields[FORM_NAMES.middle] ? formData[FORM_NAMES.middle] : 0;
      const junior = visibleFields[FORM_NAMES.junior] ? formData[FORM_NAMES.junior] : 0;
      const numberOfGuests = visibleFields[FORM_NAMES.numberOfGuests]
        ? formData[FORM_NAMES.numberOfGuests]
        : 0;

      const payload = {
        seniors: senior,
        adults: middle || numberOfGuests,
        children: junior,
        group_type: getGroupType(numberOfGuests, formData[FORM_NAMES.group_type]?.value),
      };

      patchGuestGroup(payload, {
        onSettled: onClose,
        onSuccess: () => {
          if (hasPetFeeOffer) {
            void updateReservation({
              payload: {number_of_pets: formData[FORM_NAMES.numberOfPets] ?? 0},
            });
            createPetFeeDeal(formData[FORM_NAMES.numberOfPets] ?? 0);
          }
          onClose();
        },
      });
    },
    [
      createPetFeeDeal,
      hasPetFeeOffer,
      onClose,
      patchGuestGroup,
      seasonEntity,
      updateReservation,
    ],
  );

  const onSubmit = (submittingForm: MODAL_FORMS) => (formData: FormTypes) => {
    const numberOfGuests =
      formData[FORM_NAMES.numberOfGuests] ?? guestGroup?.known_number_of_guests;

    const isNumberOfGuestForm = submittingForm === MODAL_FORMS.numberOfGuests;
    const isReservationTypeForm = submittingForm === MODAL_FORMS.reservationType;
    const shouldSwitchToReservationTypeForm =
      numberOfGuests > 1 &&
      availableForms[MODAL_FORMS.reservationType] &&
      !isReservationTypeForm;

    const shouldSwitchToConfirmLeaderGuestForm =
      isNumberOfGuestForm && availableForms[MODAL_FORMS.confirmLeaderGuest];

    if (shouldSwitchToConfirmLeaderGuestForm) {
      setCurrentModalForm(MODAL_FORMS.confirmLeaderGuest);
      return;
    }

    if (shouldSwitchToReservationTypeForm) {
      setCurrentModalForm(MODAL_FORMS.reservationType);
      return;
    }

    updateGuestGroup(formData);
  };

  const onConfirmLeaderGuest = async (showInvoiceFields?: boolean) => {
    await mutate({payload: {show_invoicing_fields: showInvoiceFields}});

    if (availableForms[MODAL_FORMS.reservationType]) {
      setCurrentModalForm(MODAL_FORMS.reservationType);
      return;
    }
    handleSubmit(onSubmit(MODAL_FORMS.confirmLeaderGuest))();
  };

  const getContentClassName = () => {
    const baseClass =
      'px-6 pb-6 pt-4 sm:px-8 sm:pb-8 sm:pt-8 kiosko:p-14 [&>div:first-child]:kiosko:text-[38px]';
    if (currentModalForm === MODAL_FORMS.reservationType) {
      return cn(baseClass, 'sm:w-[404px]');
    }
    if (currentModalForm === MODAL_FORMS.confirmLeaderGuest) {
      return cn(baseClass, 'sm:w-[375px]');
    }
    if (isWelcome) {
      return cn(baseClass, 'sm:w-[520px] sm:px-10 sm:pb-10 sm:pt-11');
    }
    return cn(baseClass, 'sm:w-[430px]');
  };

  return (
    <ResponsiveSheet
      open={isOpen}
      onClose={onClose}
      title={
        isWelcome
          ? housingName
            ? t('welcome_to_property', {property: housingName})
            : t('welcome')
          : title
      }
      description={isWelcome ? welcomeDescription : description}
      className={cn('who-coming-sheet w-auto')}
      contentClassName={getContentClassName()}
      titleClassName={
        currentModalForm === MODAL_FORMS.reservationType ? 'mb-8' : undefined
      }
      descriptionClassName="text-[14px] leading-snug text-[var(--chekin-text-secondary)]"
      showCloseButton={!isWelcome}
      disableDrag={isWelcome}
      closeOnOverlayClick={!isAutoOpened}
      closeOnEscape={!isAutoOpened}
    >
      <div className="who-coming-sheet__content flex w-full flex-col transition-opacity duration-300">
        <OverlayLoader
          isLoading={isSubmitting}
          showText={false}
          loaderSize="lg"
          overlayClassName="bg-white"
          className="mt-0"
        >
          {isInitialLoading ? (
            <SettingsFormSkeleton
              rows={currentModalForm === MODAL_FORMS.numberOfGuests ? 2 : 1}
            />
          ) : (
            <FormProvider {...formMethods}>
              {currentModalForm === MODAL_FORMS.numberOfGuests &&
                (isWelcome ? (
                  <WhosComingForm
                    seasonEntity={seasonEntity}
                    guestGroup={guestGroup}
                    onSubmit={handleSubmit(onSubmit(MODAL_FORMS.numberOfGuests))}
                    ageLimit={ageLimit}
                    disabled={disabled}
                    isLoading={isLoading}
                    canEdit={canEdit}
                    showPetsCounter={hasPetFeeOffer}
                    stickySubmit={!isKiosko}
                  />
                ) : (
                  <NumberOfGuestsForm
                    seasonEntity={seasonEntity}
                    guestGroup={guestGroup}
                    onSubmit={handleSubmit(onSubmit(MODAL_FORMS.numberOfGuests))}
                    ageLimit={ageLimit}
                    disabled={disabled}
                    isLoading={isLoading}
                    canEdit={canEdit}
                    showPetsCounter={hasPetFeeOffer}
                    stickySubmit={!isKiosko}
                    submitLabel={submitLabel}
                    icons={COUNTER_ICONS}
                  />
                ))}
              {currentModalForm === MODAL_FORMS.confirmLeaderGuest && (
                <ConfirmLeaderGuestForm
                  onSaveGuest={() => setIsLoadingSaveGuest(true)}
                  onConfirm={onConfirmLeaderGuest}
                  onFinish={() => setIsLoadingSaveGuest(false)}
                  closeModal={onClose}
                  isLoading={isLoading}
                />
              )}
              {currentModalForm === MODAL_FORMS.reservationType && (
                <ReservationTypeForm
                  onSubmit={handleSubmit(onSubmit(MODAL_FORMS.reservationType))}
                  groupType={groupType}
                  disabled={disabled}
                  submitLabel={submitLabel}
                />
              )}

              {Boolean(guestGroupMutationError?.errors?.[0]?.message) && (
                <div className="who-coming-sheet__error">
                  <p className="text-left text-[11px] text-[var(--chekin-red-soft)]">
                    {guestGroupMutationError?.errors[0].message}
                  </p>
                </div>
              )}
            </FormProvider>
          )}
        </OverlayLoader>
      </div>
    </ResponsiveSheet>
  );
}
export {ReservationSettingsModal};
